import { useCallback, useEffect, useState } from 'react';
import { formatMoney } from '@/lib/credit';
import { getStoreBalance, getWalletBalance, resolveCreditCustomerId } from '@/lib/storeBalance';

interface Props {
  merchantId: number | null;
  /** users.id du client (compte Zifek) — null si le client n'est pas rattaché. */
  clientUserId: number | null;
  /** Id du carnet de crédit si connu (évite une résolution). */
  creditCustomerId?: number | null;
  currency?: string;
  /** Changez cette valeur pour forcer un rafraîchissement après une opération. */
  refreshKey?: number;
  /** Masquer le portefeuille Zifek (réservé au commerçant). */
  showWallet?: boolean;
}

/**
 * ClientBalanceCards — affiche les deux soldes d'un client :
 *  - « Solde magasin » : fonds déposés, utilisables uniquement chez ce commerçant.
 *  - « Solde portefeuille Zifek » : solde utilisable partout sur les sites Zifek.
 */
export default function ClientBalanceCards({
  merchantId,
  clientUserId,
  creditCustomerId = null,
  currency = 'MAD',
  refreshKey = 0,
  showWallet = true,
}: Props) {
  const [store, setStore] = useState(0);
  const [debt, setDebt] = useState(0);
  const [wallet, setWallet] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let creditId = creditCustomerId;
      if (creditId == null && merchantId && clientUserId != null) {
        creditId = await resolveCreditCustomerId(merchantId, clientUserId);
      }
      const [storeRes, walletRes] = await Promise.all([
        merchantId && creditId != null
          ? getStoreBalance(merchantId, creditId)
          : Promise.resolve({ store: 0, debt: 0 }),
        getWalletBalance(clientUserId),
      ]);
      setStore(storeRes.store);
      setDebt(storeRes.debt);
      setWallet(walletRes);
    } catch {
      setStore(0);
      setDebt(0);
      setWallet(0);
    } finally {
      setLoading(false);
    }
  }, [merchantId, clientUserId, creditCustomerId]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  return (
    <div className={`grid grid-cols-1 ${showWallet ? 'sm:grid-cols-2' : ''} gap-3`}>
      {/* Solde magasin */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
        <div className="flex items-center gap-2 text-foreground-400 mb-1.5">
          <span className="w-6 h-6 rounded-md bg-secondary-100 text-secondary-700 flex items-center justify-center">
            <i className="ri-store-2-line text-xs"></i>
          </span>
          <span className="text-[11px] font-semibold uppercase tracking-wider">Solde magasin</span>
        </div>
        <div className="text-xl font-bold font-heading text-secondary-700">
          {loading ? '—' : formatMoney(store, currency)}
        </div>
        <p className="text-[11px] text-foreground-400 mt-1">
          {debt > 0 ? `Dette en cours : ${formatMoney(debt, currency)} · ` : ''}
          Utilisable uniquement chez ce commerce
        </p>
      </div>

      {/* Solde portefeuille Zifek */}
      {showWallet && (
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
          <div className="flex items-center gap-2 text-foreground-400 mb-1.5">
            <span className="w-6 h-6 rounded-md bg-accent-100 text-accent-700 flex items-center justify-center">
              <i className="ri-wallet-3-line text-xs"></i>
            </span>
            <span className="text-[11px] font-semibold uppercase tracking-wider">Solde portefeuille Zifek</span>
          </div>
          <div className="text-xl font-bold font-heading text-accent-700">
            {loading ? '—' : formatMoney(wallet, currency)}
          </div>
          <p className="text-[11px] text-foreground-400 mt-1">Utilisable partout sur les sites Zifek</p>
        </div>
      )}
    </div>
  );
}